/**
 * 易宝收银台静默余额支付（WALLET_BALANCE）。
 * 逆向自 cash.yeepay.com mobile-cashier app.js：AES-CBC + PKCS7，前缀 AES。
 */
import { Capacitor, CapacitorHttp } from "@capacitor/core";
import { extractPayCashierUrl, extractOrderPayStr } from "@/utils/yeepayCashier";

const UUID_TOKEN_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const YEEPAY_HOST = "https://cash.yeepay.com";
const YEEPAY_AES_KEY = "db2139561c9fe068";
const YEEPAY_AES_IV = "db2139561c9fe067";
const WALLET_PAY_PATH = "/newh5/ajax/request/wallet/pay";
const SUPPORT_BANK_PATH = "/wallet/supportbanklist";
const QUERY_RESULT_PATH = "/newh5/ajax/query/result";

const UA = "Mozilla/5.0 (iPhone; CPU iPhone OS 18_5 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/18.5 Mobile/15E148 Safari/604.1";

function yeepayBase() {
    if (typeof window !== "undefined" && !Capacitor.isNativePlatform()) {
        return "/yeepay-cash";
    }
    return YEEPAY_HOST;
}

function pkcs7Pad(bytes, blockSize = 16) {
    const pad = blockSize - (bytes.length % blockSize || blockSize);
    const out = new Uint8Array(bytes.length + pad);
    out.set(bytes);
    out.fill(pad, bytes.length);
    return out;
}

function bufToHex(buf) {
    return Array.from(new Uint8Array(buf))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
}

/** 6 位支付密码 → AES 密文（易宝 H5 同款） */
export async function encryptYeepayTradePassword(plain) {
    const pwd = String(plain ?? "").trim();
    if (!/^\d{6}$/.test(pwd)) {
        throw new Error("易宝支付密码须为 6 位数字");
    }
    const enc = new TextEncoder();
    const key = await crypto.subtle.importKey(
        "raw",
        enc.encode(YEEPAY_AES_KEY),
        { name: "AES-CBC" },
        false,
        ["encrypt"]
    );
    const padded = pkcs7Pad(enc.encode(pwd));
    const ct = await crypto.subtle.encrypt(
        { name: "AES-CBC", iv: enc.encode(YEEPAY_AES_IV) },
        key,
        padded
    );
    return `AES${bufToHex(ct).toUpperCase()}`;
}

function isYeepaySessionToken(token) {
    return UUID_TOKEN_RE.test(String(token ?? "").trim());
}

/** 从 URL 查询参数提取易宝会话 token（UUID，非 orderStr 里的 64 位 hex） */
export function tokenFromYeepayUrl(raw) {
    const s = String(raw ?? "").trim();
    if (!s)
        return "";
    try {
        const u = new URL(s);
        const q = u.searchParams.get("token");
        if (q && isYeepaySessionToken(q))
            return q.trim();
        const m = u.pathname.match(/\/request\/([0-9a-f-]{36})/i);
        if (m && isYeepaySessionToken(m[1]))
            return m[1];
    }
    catch {
        const qm = s.match(/[?&#]token=([0-9a-f-]{8}-[0-9a-f-]{4}-[0-9a-f-]{4}-[0-9a-f-]{4}-[0-9a-f-]{12})/i);
        if (qm)
            return qm[1];
    }
    return "";
}

function toProxyYeepayUrl(absUrl) {
    const u = new URL(absUrl);
    return `${yeepayBase()}${u.pathname}${u.search}`;
}

function absYeepayUrl(loc) {
    const s = String(loc ?? "").trim();
    if (!s)
        return "";
    if (/^https?:\/\//i.test(s))
        return s;
    return `${YEEPAY_HOST}${s.startsWith("/") ? "" : "/"}${s}`;
}

/**
 * 镜权 order/pay 返回 cashier/std?token=<64hex>，须 GET 该链接跟随重定向到
 * newwap/?token=<uuid> 后，才能调 wallet/pay（与 H5 收银台一致）。
 */
export async function resolveYeepayWalletToken(source) {
    const orderStr = typeof source === "string"
        ? source.trim()
        : (extractOrderPayStr(source) || extractPayCashierUrl(source));
    if (!orderStr)
        return "";

    const direct = tokenFromYeepayUrl(orderStr);
    if (direct)
        return direct;

    const cashierUrl = /^https?:\/\//i.test(orderStr)
        ? orderStr
        : `${YEEPAY_HOST}/${orderStr.replace(/^\//, "")}`;

    let url = Capacitor.isNativePlatform() ? cashierUrl : toProxyYeepayUrl(cashierUrl);
    const headers = {
        Accept: "text/html,application/xhtml+xml,*/*",
        "User-Agent": UA
    };

    for (let hop = 0; hop < 6; hop++) {
        if (Capacitor.isNativePlatform()) {
            const res = await CapacitorHttp.get({
                url,
                headers,
                disableRedirects: true
            });
            const status = Number(res.status ?? 0);
            const fromFinal = tokenFromYeepayUrl(res.url || "");
            if (fromFinal)
                return fromFinal;
            const loc = res.headers?.Location || res.headers?.location;
            if (status >= 300 && status < 400 && loc) {
                const t = tokenFromYeepayUrl(loc);
                if (t)
                    return t;
                url = absYeepayUrl(loc);
                continue;
            }
            break;
        }

        const res = await fetch(url, { redirect: "manual" });
        if (res.status >= 300 && res.status < 400) {
            const loc = res.headers.get("Location");
            if (!loc)
                break;
            const t = tokenFromYeepayUrl(loc);
            if (t)
                return t;
            url = toProxyYeepayUrl(absYeepayUrl(loc));
            continue;
        }
        const t = tokenFromYeepayUrl(res.url || "");
        if (t)
            return t;
        break;
    }

    if (!Capacitor.isNativePlatform() && typeof window !== "undefined") {
        try {
            const r = await fetch(`/api/yeepay-resolve-token?url=${encodeURIComponent(cashierUrl)}`);
            const j = await r.json();
            const t = tokenFromYeepayUrl(j?.finalUrl || "") || String(j?.token || "").trim();
            if (isYeepaySessionToken(t))
                return t;
        }
        catch (e) {
            console.warn("[yeepayWallet] dev resolve fallback failed", e);
        }
    }
    return "";
}

/** @deprecated 请用 resolveYeepayWalletToken；此处仅兼容旧调用 */
export function extractYeepayToken(source) {
    if (!source)
        return "";
    if (typeof source === "string")
        return tokenFromYeepayUrl(source);
    const url = extractPayCashierUrl(source) || extractOrderPayStr(source);
    return url ? tokenFromYeepayUrl(url) : "";
}

async function yeepayFormPost(path, form, referer) {
    const url = `${yeepayBase()}${path}`;
    const body = new URLSearchParams(form).toString();
    const headers = {
        Accept: "application/json, text/plain, */*",
        "Content-Type": "application/x-www-form-urlencoded",
        Origin: YEEPAY_HOST,
        Referer: referer || `${YEEPAY_HOST}/newwap/mobile-cashier/index.html`,
        "User-Agent": UA
    };
    if (Capacitor.isNativePlatform()) {
        const res = await CapacitorHttp.post({ url, headers, data: body });
        const data = res.data;
        if (typeof data === "string") {
            try {
                return JSON.parse(data);
            }
            catch {
                return { _raw: data };
            }
        }
        return data;
    }
    const res = await fetch(url, { method: "POST", headers, body });
    const text = await res.text();
    try {
        return JSON.parse(text);
    }
    catch {
        return { _raw: text, _status: res.status };
    }
}

/** 查询易宝支付结果 */
export async function queryYeepayPayResult(token) {
    return yeepayFormPost(QUERY_RESULT_PATH, { token: String(token) });
}

/**
 * 易宝钱包余额静默支付（默认 WALLET_BALANCE，bindId 为空）。
 * @returns {{ ok: boolean, bizStatus?: string, raw: object }}
 */
export async function yeepayWalletBalancePay(token, tradePasswordPlain, referer) {
    await yeepayFormPost(SUPPORT_BANK_PATH, { token: String(token) }, referer);
    const tradePassword = await encryptYeepayTradePassword(tradePasswordPlain);
    const payRes = await yeepayFormPost(WALLET_PAY_PATH, {
        token: String(token),
        bindId: "",
        payType: "WALLET_BALANCE",
        tradePassword
    }, referer);
    const bizStatus = String(payRes?.bizStatus ?? payRes?.data?.bizStatus ?? "").toLowerCase();
    const errMsg = String(payRes?.errormsg ?? payRes?.errorMsg ?? "").trim();
    if (bizStatus === "success") {
        return { ok: true, bizStatus: "success", raw: payRes };
    }
    return {
        ok: false,
        bizStatus,
        error: errMsg || `易宝扣款失败(${payRes?.errorcode || bizStatus || "unknown"})`,
        raw: payRes
    };
}

/**
 * 镜权 order/pay 之后：解析 token → 易宝余额静默扣款。
 */
export async function yeepaySilentBalancePayFromOrderPay(orderPayRes, tradePasswordPlain) {
    const orderStr = extractOrderPayStr(orderPayRes) || extractPayCashierUrl(orderPayRes);
    if (!orderStr) {
        return { ok: false, error: "未找到易宝 orderStr", token: "" };
    }

    if (!Capacitor.isNativePlatform() && typeof window !== "undefined") {
        try {
            const r = await fetch("/api/yeepay-silent-pay", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ orderStr, tradePassword: String(tradePasswordPlain ?? "").trim() })
            });
            const j = await r.json();
            return {
                ok: Boolean(j?.ok),
                bizStatus: j?.bizStatus,
                error: j?.error,
                token: j?.token || "",
                raw: j?.raw
            };
        }
        catch (e) {
            return { ok: false, error: e?.message || "易宝静默支付请求失败", token: "" };
        }
    }

    const token = await resolveYeepayWalletToken(orderPayRes);
    if (!token) {
        return { ok: false, error: "未解析到易宝会话 token（cashier 重定向失败）", token: "" };
    }
    const referer = `${YEEPAY_HOST}/newwap/?token=${encodeURIComponent(token)}`;
    const paid = await yeepayWalletBalancePay(token, tradePasswordPlain, referer);
    return { ...paid, token };
}
